import {
  Badge,
  Button,
  cn,
  EmptyState,
  inputBaseClass,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@paguz/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { SearchIcon, UserSearchIcon } from 'lucide-react';
import { useState } from 'react';
import { api, queryKeys } from '../../api/endpoints.ts';
import { ErrorState, LoadingRows, PageHeader, Panel } from '../../components/page.tsx';
import { PHASE_LABEL } from '../../lib/labels.ts';

const route = getRouteApi('/app/clientes');

const CUSTOMER_STATUS = {
  ACTIVE: { label: 'Activo', tone: 'success' },
  PROSPECT: { label: 'Prospecto', tone: 'secondary' },
  BLOCKED: { label: 'Bloqueado', tone: 'danger' },
} as const;

export function CustomersPage() {
  const { q = '' } = route.useSearch();
  const navigate = route.useNavigate();
  const [draft, setDraft] = useState(q);
  const query = useQuery({
    queryKey: queryKeys.customers(q),
    queryFn: () => api.listCustomers(q),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader
        title="Clientes y líneas"
        description="Busca por nombre, documento, celular, cus_ o ln_. La PII se muestra enmascarada según tu rol."
      />
      <form
        role="search"
        className="mb-5 flex max-w-2xl gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          void navigate({ search: { q: draft.trim() || undefined } });
        }}
      >
        <div className="relative flex-1">
          <SearchIcon className="pointer-events-none absolute top-1/2 left-3.5 size-5 -translate-y-1/2 text-muted-foreground" />
          <input
            aria-label="Buscar clientes"
            className={cn(inputBaseClass, 'pl-11')}
            placeholder="Rosa, 52111222, 300 111 2233, cus_…"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
          />
        </div>
        <Button type="submit" variant="highlight" size="lg">
          Buscar
        </Button>
      </form>

      <Panel bodyClassName="p-0">
        {query.isPending ? (
          <div className="p-5">
            <LoadingRows rows={4} />
          </div>
        ) : query.isError ? (
          <div className="p-5">
            <ErrorState error={query.error} onRetry={() => void query.refetch()} />
          </div>
        ) : query.data.data.length === 0 ? (
          <div className="p-5">
            <EmptyState
              icon={<UserSearchIcon />}
              title="No encontramos clientes"
              description="Revisa el nombre o prueba con el documento o el celular completos."
            />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead>Documento</TableHead>
                <TableHead>Celular</TableHead>
                <TableHead>Negocio</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Líneas</TableHead>
                <TableHead>Solicitud</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.data.data.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <Link
                      to="/clientes/$customerId"
                      params={{ customerId: c.id }}
                      className="font-semibold text-primary hover:underline"
                    >
                      {c.fullName}
                    </Link>
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {c.documentType} {c.maskedDocument}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{c.maskedPhone}</TableCell>
                  <TableCell>
                    <div>{c.businessName ?? '—'}</div>
                    <div className="text-xs text-muted-foreground">{c.city}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={CUSTOMER_STATUS[c.status].tone}>
                      {CUSTOMER_STATUS[c.status].label}
                    </Badge>
                  </TableCell>
                  <TableCell className="tabular-nums">{c.lineIds.length}</TableCell>
                  <TableCell>
                    {c.activeApplication ? (
                      <Badge variant="highlight">
                        En curso ·{' '}
                        {PHASE_LABEL[c.activeApplication.phase] ?? c.activeApplication.phase}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>
    </>
  );
}

export { CUSTOMER_STATUS };
